<script>
  import { onMount, tick } from "svelte";
  import CenteredMenuDialog from "../../../components/layout/menuDialog/variants/CenteredMenuDialog.svelte";
  import MenuDialogSectionEntry from "../../../components/layout/menuDialog/MenuDialogSectionEntry.svelte";
  import { translate } from "../../../i18n/i18next.js";
  import ToggleSwitch from "../../../components/input/ToggleSwitch.svelte";
  import { updateProjectSettings } from "../../../components/ganttchart/ganttStore/connectProjectAdminSettings.js";
  import { getFetchErrorHandlerContext } from "../../../hooks/useFetchErrorHandlerContext.js";
  import { useInvalidateProjectQuery } from "../../../hooks/useProject.js";
  import DialogTitle from "../../../components/dialog/DialogTitle.svelte";
  import DialogSplitter from "../../../components/dialog/DialogSplitter.svelte";
  import DialogContent from "../../../components/dialog/DialogContent.svelte";
  import DialogBottomControls from "../../../components/dialog/DialogBottomControls.svelte";
  import DialogBottomControlButton from "../../../components/dialog/DialogBottomControlButton.svelte";
  import MenuDialogSectionEntryTitle from "../../../components/layout/menuDialog/MenuDialogSectionEntryTitle.svelte";
  import HelpTooltip from "../../../components/tooltip/HelpTooltip.svelte";
  import { useMyProjectPermission } from "../../../hooks/useMyProjectPermission.js";

  export let GanttStore
  export let onClose

  const {
    IsAutoUpdateEnabled,
    PreventMistakes,
    ProjectId,
  } = GanttStore

  const {handleNonBlockingFetchErrors} = getFetchErrorHandlerContext()
  const {invalidateProjectQuery} = useInvalidateProjectQuery()
  const {authorize} = useMyProjectPermission($ProjectId)

  let dialog

  function closeDialog() {
    dialog.close()
    onClose()
  }

  async function handleChangeSettings(name, value) {
    await updateProjectSettings(
      $ProjectId,
      {[name]: value},
      handleNonBlockingFetchErrors,
      () => invalidateProjectQuery()
    )
  }

  onMount(async () => {
    await tick()
    dialog.showModal()
  })
</script>

<CenteredMenuDialog
  bind:dialog={dialog}
  onClose={closeDialog}
>
  <DialogTitle>
    {translate("frontend:generic.projectAdminSettings.title")}
    <HelpTooltip title={translate("frontend:generic.projectAdminSettings.title")}>
      {translate("frontend:generic.projectAdminSettings.tooltip")}
    </HelpTooltip>
  </DialogTitle>
  <DialogSplitter/>
  <DialogContent>
    <MenuDialogSectionEntry>
      <div class="toggle-switch-container">
        <MenuDialogSectionEntryTitle>
          {translate("frontend:ganttChart.settings.autoUpdate.label")}
          <HelpTooltip title={translate("frontend:ganttChart.settings.autoUpdate.label")}>
            {translate("frontend:ganttChart.settings.autoUpdate.tooltip")}
          </HelpTooltip>
        </MenuDialogSectionEntryTitle>
        <div class="toggle-switch">
          <ToggleSwitch
            disabled={!$authorize("ProjectAdmin")}
            on={$IsAutoUpdateEnabled}
            onChange={async (e) => {
              IsAutoUpdateEnabled.set(e.target.checked)
              await handleChangeSettings("isAutoUpdateEnabled", e.target.checked)
            }}
          />
        </div>
      </div>
    </MenuDialogSectionEntry>
    <MenuDialogSectionEntry>
      <div class="toggle-switch-container">
        <MenuDialogSectionEntryTitle>
          {translate("frontend:ganttChart.settings.preventMistakes.label")}
          <HelpTooltip title={translate("frontend:ganttChart.settings.preventMistakes.label")}>
            {translate("frontend:ganttChart.settings.preventMistakes.tooltip")}
          </HelpTooltip>
        </MenuDialogSectionEntryTitle>
        <div class="toggle-switch">
          <ToggleSwitch
            disabled={!$authorize("ProjectAdmin")}
            on={$PreventMistakes}
            onChange={async e => {
              PreventMistakes.set(e.target.checked)
              await handleChangeSettings("isPreventMistakeEnabled", e.target.checked)
            }}
          />
        </div>
      </div>
    </MenuDialogSectionEntry>
  </DialogContent>
  <DialogSplitter/>
  <DialogBottomControls>
    <DialogBottomControlButton type="primary" onClick={onClose}>OK</DialogBottomControlButton>
  </DialogBottomControls>
</CenteredMenuDialog>

<style>
  .toggle-switch-container {
    padding-left: 1rem;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
  }
  .toggle-switch {
    margin-left: auto;
  }
</style>
